<script setup>
import { ref, provide, onMounted, onUnmounted, nextTick } from 'vue'
import { useBreakpoint } from '@/composables/useBreakpoint'

defineProps({
  align: { type: String, default: 'right', validator: v => ['left', 'right'].includes(v) },
})

const { isMobile } = useBreakpoint()

const isOpen = ref(false)
const root = ref(null)
const trigger = ref(null)

function open() { isOpen.value = true }
function close() { isOpen.value = false }
function toggle() { isOpen.value ? close() : open() }

function onDocClick(e) {
  if (isMobile.value) return  // backdrop handles outside-click on mobile
  if (root.value && !root.value.contains(e.target)) close()
}
function onKey(e) {
  if (!isOpen.value) return
  if (e.key === 'Escape') {
    close()
    nextTick(() => trigger.value?.focus?.())
  }
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onKey)
})
onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onKey)
})

provide('uiMenuClose', close)
</script>

<template>
  <div
    ref="root"
    class="relative inline-block"
  >
    <div
      ref="trigger"
      @click.stop
    >
      <slot
        name="trigger"
        :toggle="toggle"
        :open="open"
        :close="close"
        :is-open="isOpen"
      />
    </div>
    <Teleport to="body">
      <div
        v-if="isMobile && isOpen"
        class="fixed inset-0 z-[var(--z-dropdown)] bg-black/30"
        @click="close"
      />
    </Teleport>
    <Teleport
      to="body"
      :disabled="!isMobile"
    >
      <div
        v-if="isOpen"
        role="menu"
        :class="[
          'bg-[color:var(--surface)] border border-[color:var(--line)] shadow-[var(--shadow-md)] z-[var(--z-dropdown)] py-1',
          isMobile
            ? 'fixed inset-x-0 bottom-0 rounded-t-[var(--radius-md)] pb-[max(env(safe-area-inset-bottom),0.5rem)]'
            : 'absolute top-full mt-1 min-w-44 rounded-[var(--radius-md)]',
          !isMobile && align === 'right' ? 'right-0' : '',
          !isMobile && align === 'left' ? 'left-0' : '',
        ]"
      >
        <slot />
      </div>
    </Teleport>
  </div>
</template>
